// Records that a player was here today, once per Lagos day per browser:
// users/{uid}.lastActiveAt, .activeDays (YYYY-MM-DD strings) and .lastPlatform.
//
// The admin dashboard reads these for daily active players and retention
// (functions/admin.js). lastActiveAt is the same field mobile writes and the
// comeback notifications read, so a mobile player who plays on web isn't
// told they've been away. New accounts get joinDay and their first
// activeDays entry in authService.createUserDocument, which is what lets
// retention count only players tracked since the day they joined.
import { doc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const STORAGE_KEY = "nameit.activity.day";
const LAGOS_OFFSET_MS = 60 * 60 * 1000;

/** The Lagos calendar day (UTC+1) as YYYY-MM-DD. Matches functions/adminMetrics.js. */
export function lagosDay(date = new Date()) {
  return new Date(date.getTime() + LAGOS_OFFSET_MS).toISOString().slice(0, 10);
}

/** Fields a brand-new web account starts with. */
export function newAccountActivity(now = new Date()) {
  const today = lagosDay(now);
  return { joinDay: today, activeDays: [today], lastActiveAt: now, lastPlatform: "web", firstPlatform: "web" };
}

export async function recordActivity(uid, deps = {}) {
  const api = { db, doc, updateDoc, arrayUnion, serverTimestamp, storage: safeStorage(), now: new Date(), ...deps };
  if (!uid) return false;
  const today = lagosDay(api.now);
  const stamp = `${uid}:${today}`;
  if (api.storage?.getItem(STORAGE_KEY) === stamp) return false;

  try {
    await api.updateDoc(api.doc(api.db, "users", uid), {
      lastActiveAt: api.serverTimestamp(),
      activeDays: api.arrayUnion(today),
      lastPlatform: "web",
    });
    api.storage?.setItem(STORAGE_KEY, stamp);
    return true;
  } catch {
    // Usually the account's document doesn't exist yet; createUserDocument
    // records today when it creates it. Either way this is not worth
    // bothering the player about.
    return false;
  }
}

function safeStorage() {
  try {
    const s = window.localStorage;
    return {
      getItem: (k) => {
        try {
          return s.getItem(k);
        } catch {
          return null;
        }
      },
      setItem: (k, v) => {
        try {
          s.setItem(k, v);
        } catch {
          /* private mode */
        }
      },
    };
  } catch {
    return null;
  }
}
